import React, { useEffect, useRef, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./editPropertyResaleVibe.css";
import {
  viewPropertyResaleVibe,
  updatePropertyResaleVibe,
} from "../../Redux/Actions/propertyResaleVibeActions";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCategoryList,
  viewSubCategoryList,
} from "../../Redux/Actions/categoryResaleActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";

const TYPE_OPTIONS = [
  { value: "business", label: "Business", emoji: "💼" },
  { value: "resale", label: "Resale", emoji: "🔄" },
];

const EditPropertyResaleVibe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const iconInputRef = useRef(null);

  const { categories } = useSelector((state) => state.categoriesResale);
  const { loading = false, vibeDetail } = useSelector(
    (state) => state.propertyResaleVibe || {}
  );

  const [vibeData, setVibeData] = useState({
    _id: "",
    category_id: "",
    sub_category: "",
    type: [],
    name: "",
    icon: "",
  });

  const [subCategories, setSubCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [iconPreview, setIconPreview] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconDragOver, setIconDragOver] = useState(false);

  useEffect(() => {
    dispatch(viewCategoryList({ page: 1, limit: 100 }));
    if (id) dispatch(viewPropertyResaleVibe(id));
  }, [dispatch, id]);

  useEffect(() => {
    const details = vibeDetail?.data;
    if (!details) return;

    const parentId = details.category?._id || details.category || "";
    const subCatId = details.category_id?._id || details.category_id || "";

    if (details.icon) setIconPreview(details.icon);

    setVibeData({
      _id: details._id || "",
      category_id: parentId,
      sub_category: subCatId,
      type: Array.isArray(details.type)
        ? details.type
        : details.type
        ? [details.type]
        : [],
      name: details.name || "",
      icon: details.icon || "",
    });

    if (parentId) {
      dispatch(
        viewSubCategoryList({ page: 1, limit: 100, category_id: parentId })
      ).then((result) => {
        const list = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.data)
          ? result.data.data
          : [];
        setSubCategories(list);
      });
    }
  }, [dispatch, vibeDetail]);

  useEffect(() => {
    if (!vibeData.category_id) {
      setSubCategories([]);
      return;
    }

    dispatch(
      viewSubCategoryList({
        page: 1,
        limit: 100,
        category_id: vibeData.category_id,
      })
    ).then((result) => {
      const list = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.data?.data)
        ? result.data.data
        : [];
      setSubCategories(list);
    });
  }, [dispatch, vibeData.category_id]);

  const clearError = (field) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "category_id") {
      setVibeData((prev) => ({
        ...prev,
        category_id: value,
        sub_category: "",
      }));
      clearError("category_id");
      clearError("sub_category");
      return;
    }

    setVibeData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const toggleType = (value) => {
    setVibeData((prev) => ({
      ...prev,
      type: prev.type.includes(value)
        ? prev.type.filter((t) => t !== value)
        : [...prev.type, value],
    }));
    clearError("type");
  };

  const processIconFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const preview = URL.createObjectURL(file);
    setIconPreview(preview);

    try {
      setUploadingIcon(true);
      const url = await AddImage(file);
      setVibeData((prev) => ({ ...prev, icon: url }));
      clearError("icon");
    } catch (error) {
      toast.error("Icon upload failed");
      setIconPreview(vibeData.icon || null);
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleIconFileChange = (e) => {
    processIconFile(e.target.files?.[0]);
  };

  const handleIconDrop = (e) => {
    e.preventDefault();
    setIconDragOver(false);
    processIconFile(e.dataTransfer.files?.[0]);
  };

  const removeIcon = () => {
    setIconPreview(null);
    setVibeData((prev) => ({ ...prev, icon: "" }));
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const validate = () => {
    const errs = {};

    if (!vibeData.category_id) errs.category_id = "Category is required";
    if (!vibeData.sub_category) errs.sub_category = "Sub-category is required";
    if (!vibeData.type.length) errs.type = "Select at least one type";
    if (!vibeData.name.trim()) errs.name = "Vibe name is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleReset = () => {
    const details = vibeDetail?.data;
    if (!details) return;

    const parentId = details.category?._id || details.category || "";
    const subCatId = details.category_id?._id || details.category_id || "";

    setVibeData({
      _id: details._id || "",
      category_id: parentId,
      sub_category: subCatId,
      type: Array.isArray(details.type)
        ? details.type
        : details.type
        ? [details.type]
        : [],
      name: details.name || "",
      icon: details.icon || "",
    });

    setErrors({});
    setIconPreview(details.icon || null);

    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        _id: vibeData._id || id,
        category: vibeData.category_id,
        category_id: vibeData.sub_category,
        type: vibeData.type,
        name: vibeData.name,
        icon: vibeData.icon,
      };

      const response = await dispatch(updatePropertyResaleVibe(payload));

      if (response?.statusCode === 200) {
        toast.success("Property resale vibe updated successfully!");
        navigate("/resale_vibe");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update property resale vibe");
    }
  };

  return (
    <MainLayout>
      <div className="epv-wrapper">
        <div className="epv-header">
          <div className="epv-header-left">
            <div className="epv-header-icon">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <div>
              <h1 className="epv-title">Edit Property Resale Vibe</h1>
              <p className="epv-subtitle">
                Update the resale vibe configuration
              </p>
            </div>
          </div>

          <Link to="/resale_vibe" className="epv-view-btn">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View Vibes
          </Link>
        </div>

        <form className="epv-form-card" onSubmit={handleSubmit}>
          <div className="epv-section">
            <div className="epv-section-header">
              <span className="epv-section-badge">01</span>
              <h2 className="epv-section-title">Basic Information</h2>
            </div>

            <div className="epv-grid">
              <div className="epv-field">
                <label className="epv-label">
                  Category <span className="epv-required">*</span>
                </label>
                <select
                  className={`epv-select ${
                    errors.category_id ? "epv-input--error" : ""
                  }`}
                  name="category_id"
                  value={vibeData.category_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select category</option>
                  {categories?.data?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && (
                  <span className="epv-error-msg">{errors.category_id}</span>
                )}
              </div>

              <div className="epv-field">
                <label className="epv-label">
                  Sub Category <span className="epv-required">*</span>
                </label>
                <select
                  className={`epv-select ${
                    !vibeData.category_id ? "epv-select-disabled" : ""
                  } ${errors.sub_category ? "epv-input--error" : ""}`}
                  name="sub_category"
                  value={vibeData.sub_category}
                  onChange={handleChange}
                  disabled={!vibeData.category_id || loading}
                >
                  <option value="">
                    {!vibeData.category_id
                      ? "Select category first"
                      : "Select sub category"}
                  </option>
                  {subCategories.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                {errors.sub_category && (
                  <span className="epv-error-msg">{errors.sub_category}</span>
                )}
              </div>

              <div className="epv-field">
                <label className="epv-label">
                  Type <span className="epv-required">*</span>
                </label>
                <div
                  className={`epv-type-toggle-group ${
                    errors.type ? "epv-type-toggle-group--error" : ""
                  }`}
                >
                  {TYPE_OPTIONS.map((opt) => {
                    const active = vibeData.type.includes(opt.value);

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`epv-type-chip ${
                          active ? "epv-type-chip--active" : ""
                        }`}
                        onClick={() => toggleType(opt.value)}
                        disabled={loading}
                        aria-pressed={active}
                      >
                        <span className="epv-type-chip-emoji">{opt.emoji}</span>
                        <span className="epv-type-chip-label">{opt.label}</span>
                        {active && (
                          <span className="epv-type-chip-check">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {vibeData.type.length > 0 && (
                  <div className="epv-type-selected-hint">
                    Selected:{" "}
                    {vibeData.type
                      .map(
                        (t) => TYPE_OPTIONS.find((o) => o.value === t)?.label
                      )
                      .join(", ")}
                  </div>
                )}

                {errors.type && (
                  <span className="epv-error-msg">{errors.type}</span>
                )}
              </div>

              <div className="epv-field">
                <label className="epv-label">
                  Vibe Name <span className="epv-required">*</span>
                </label>
                <input
                  className={`epv-input ${
                    errors.name ? "epv-input--error" : ""
                  }`}
                  type="text"
                  name="name"
                  value={vibeData.name}
                  onChange={handleChange}
                  placeholder="e.g. Contemporary Urban Living"
                  disabled={loading}
                />
                {errors.name && (
                  <span className="epv-error-msg">{errors.name}</span>
                )}
              </div>

              <div className="epv-field epv-field-full">
                <label className="epv-label">
                  Icon <span className="epv-optional">(Optional)</span>
                </label>

                {iconPreview ? (
                  <div className="epv-icon-preview">
                    <div className="epv-icon-preview-img-wrap">
                      {uploadingIcon && (
                        <div className="epv-icon-uploading-overlay">
                          <svg
                            className="epv-spinner"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          </svg>
                          <span>Uploading…</span>
                        </div>
                      )}
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        className="epv-icon-preview-img"
                      />
                    </div>

                    <div className="epv-icon-preview-info">
                      <span className="epv-icon-preview-label">
                        Icon uploaded
                      </span>
                      {vibeData.icon && (
                        <span className="epv-icon-preview-url">
                          {vibeData.icon.slice(0, 48)}…
                        </span>
                      )}

                      <div className="epv-icon-preview-actions">
                        <button
                          type="button"
                          className="epv-icon-change-btn"
                          onClick={() => iconInputRef.current?.click()}
                          disabled={uploadingIcon || loading}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Change
                        </button>

                        <button
                          type="button"
                          className="epv-icon-remove-btn"
                          onClick={removeIcon}
                          disabled={uploadingIcon || loading}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6M9 6V4h6v2" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>

                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleIconFileChange}
                    />
                  </div>
                ) : (
                  <div
                    className={`epv-icon-dropzone ${
                      iconDragOver ? "epv-icon-dropzone--over" : ""
                    }`}
                    onClick={() => iconInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIconDragOver(true);
                    }}
                    onDragLeave={() => setIconDragOver(false)}
                    onDrop={handleIconDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && iconInputRef.current?.click()
                    }
                  >
                    <div className="epv-icon-dropzone-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>

                    <div className="epv-icon-dropzone-text">
                      <span className="epv-icon-dropzone-title">
                        {iconDragOver ? "Drop image here" : "Upload icon image"}
                      </span>
                      <span className="epv-icon-dropzone-sub">
                        Click to browse or drag & drop · PNG, JPG, SVG, WebP
                      </span>
                    </div>

                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleIconFileChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="epv-footer">
            <div className="epv-footer-nav">
              <button
                type="submit"
                className="epv-btn epv-btn-submit"
                disabled={loading || uploadingIcon}
              >
                {loading ? (
                  "Updating…"
                ) : (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Update Property Resale Vibe
                  </>
                )}
              </button>
            </div>

            <Link
              to="/resale_vibe"
              className="epv-btn epv-btn-cancel"
              onClick={handleReset}
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditPropertyResaleVibe;